import { assign, cloneDeep, isEmpty, reduce } from 'lodash';

import type { Site } from '@sine/sine-core-api-types';

import siteService from '../services/site/site.service';

function _objectDiff(newVal: Site['settings'], oldVal: Site['settings']) {
  const oldValueEmpty = isEmpty(oldVal);

  return reduce<Site['settings'], Partial<Site['settings']>>(
    newVal,
    (result, value, key) => {
      const oldJson = !oldValueEmpty ? JSON.stringify((oldVal as any)[key]) : oldVal;
      const newJson = JSON.stringify(value);

      if (oldJson !== newJson) {
        (result as any)[key] = value;
      }

      return result;
    },
    {},
  );
}

// TODO: use swr, not this dodgy caching
let cachedSite: Site = {} as unknown as Site;

const siteDetailsService = {
  async findById(id: string, cloned = true) {
    if (cachedSite.id === id) {
      return Promise.resolve(cloneDeep(cachedSite));
    }

    const site = await siteService.findById(id);
    assign(cachedSite, site);

    if (!cloned) {
      return cachedSite;
    }

    return cloneDeep(cachedSite);
  },

  async update(id: string, props: Record<string, any>) {
    const updatedSite = await siteService.updateAdminSite(id, props);

    assign(cachedSite, updatedSite);

    return cloneDeep(cachedSite);
  },

  async updateSettings(id: string, siteSettings: Site['settings']) {
    const updatedSite = await siteService.updateSettings(id, _objectDiff(siteSettings, cachedSite.settings));

    assign(cachedSite, updatedSite);

    return cloneDeep(cachedSite);
  },

  async addVisitorType(id: string, props: Record<string, any>) {
    const updatedSite = await siteService.addVisitorType(id, props);
    assign(cachedSite, updatedSite);
    return cloneDeep(cachedSite);
  },

  async updateVisitorTypes(id: string, props: Record<string, any>) {
    const updatedSite = await siteService.updateVisitorTypes(id, props);
    assign(cachedSite, updatedSite);
    return cloneDeep(cachedSite);
  },

  async updateVisitorType(id: string, visitorTypeId: string, props: Record<string, any>) {
    const updatedSite = await siteService.updateVisitorType(id, visitorTypeId, props);
    assign(cachedSite, updatedSite);
    return cloneDeep(cachedSite);
  },

  async removeVisitorType(id: string, visitorTypeId: string) {
    const updatedSite = await siteService.removeVisitorType(id, visitorTypeId);
    assign(cachedSite, updatedSite);
    return cloneDeep(cachedSite);
  },

  async getVisitorTypeHostGroupExclusions(id: string, visitorTypeId: string) {
    const hostGroups = await siteService.getVisitorTypeHostGroupExclusions(id, visitorTypeId);

    return hostGroups;
  },

  reset() {
    cachedSite = {} as unknown as Site;
  },

  getCurrent() {
    return cachedSite;
  },
};

export default siteDetailsService;
